import { definitionImagesPath } from '@/app/constants';
import {
  DialogueSlide,
  MentorNames,
  MentorPrefixes,
  ScriptGrapheme,
  ScriptItem,
  ScriptMorpheme,
} from '@/common/types/LectioTypes';
import { mentorDialogue, playSoundsInSequence } from '@/common/utils/audio';
import { getScopeAndSequenceGraphemeFromPronunciation } from '@/common/utils/scopeAndSequence';
import { EventBus } from '@/game/GameComponents/PhaserGame/EventBus';
import {
  GameInstance,
  getDailies,
  getStudent,
} from '@/game/GameComponents/PhaserGame/GameInstance';
import { ResponsivenessManager } from '@/game/GameComponents/PhaserGame/Managers/ResponsivenessManager';
import { PhaserAudioManager } from '@/game/GameComponents/PhaserGame/Utils/PhaserAudioManager';
import { GridLayoutGroup } from '@/game/GameComponents/PhaserGame/Utils/PhaserLayout/GridLayoutGroup';
import {
  containsAlphabeticalContent,
  createDialogueSlideFromConcept,
  createDynamicTextLayout,
  DoReinforcementFeedback,
  getAnchorPlugin,
  getBiomeFromStudentLevel,
  getLessonPanelSlides,
  getRelatedConceptFromMarkup,
  isEmphasizedLetter,
  isImageLink,
  isMorpheme,
  isPhoneme,
  isRelatedConceptLink,
  sanitizeFilename,
  sanitizeLessonMarkup,
  underlineBitmapText,
} from '@/game/GameComponents/PhaserGame/Utils/PhaserUtils';
import GAME_EVENTS from '@/game/GameComponents/PhaserGame/Utils/gameEventsDef';
import { HOVER_CURSOR_PATH } from '@/game/GameComponents/Utils/GameSpriteAssetsMap';
import DefaultButton from '@/game/prefabs/GeneralUI/Buttons/DefaultButton';
import HUDButton from '@/game/prefabs/GeneralUI/Buttons/HUDButton';
import MentorUI from '@/game/prefabs/GeneralUI/MentorUI';
import DialogueOptionButton from '@/game/prefabs/Lesson/DialogueOptionButton';
import LessonPanel from '@/game/prefabs/Lesson/LessonPanel';
import {
  DialogueInstance,
  DynamicTextWrapper,
} from '@/game/prefabs/Lesson/LessonPanelTypes';
import VisualAideCard from './VisualAideCard';
import VisualAideIcon from './VisualAideIcon';

export class LessonPanelController {
  constructor(private lessonPanel: LessonPanel) {
    this.getResponsiveElements('nextLineButton').forEach(
      (nextLineButton: DefaultButton) => {
        nextLineButton.controller.setSubmissionCallback(() => {
          this.handleUserRequestedNextLine();
        });
      },
    );

    // Desktop card grid.
    this.cardGrid = new GridLayoutGroup({
      scene: lessonPanel.scene,
      elements: [],
      columns: 3,
      hPadding: 156,
      vPadding: 156,
    });
    lessonPanel.scene.add.existing(this.cardGrid);
    lessonPanel.add(this.cardGrid);
    lessonPanel.visualAidCards.add(this.cardGrid);
    // this.cardGrid.setPosition(this.visualAidCards.x, this.visualAidCards.y);

    // Mobile card grid.
    this.cardGrid_m = new GridLayoutGroup({
      scene: lessonPanel.scene,
      elements: [],
      columns: 8,
      hPadding: 64,
      vPadding: 64,
    });
    lessonPanel.scene.add.existing(this.cardGrid_m);
    lessonPanel.add(this.cardGrid_m);
    lessonPanel.visualAidCards_m.add(this.cardGrid_m);

    // Change main text to a dynamic bitmap text.
    lessonPanel.dialogue_text.setVisible(false);
    const dynamicDialogueText = lessonPanel.scene.add.dynamicBitmapText(
      lessonPanel.dialogue_text.x,
      lessonPanel.dialogue_text.y,
      lessonPanel.dialogue_text.font,
      lessonPanel.dialogue_text.text,
      lessonPanel.dialogue_text.fontSize,
    );
    dynamicDialogueText.tintTopLeft = 5387071;
    dynamicDialogueText.tintTopRight = 5387071;
    dynamicDialogueText.tintBottomLeft = 5387071;
    dynamicDialogueText.tintBottomRight = 5387071;
    dynamicDialogueText.text =
      'Welcome spellcaster to my realm! Today we are going to be learning about phonemes.';
    dynamicDialogueText.fontSize = -24;
    dynamicDialogueText.maxWidth = 600;
    lessonPanel.mainContentContainer.add(dynamicDialogueText);
    this.dynamicDialogueTextWrapper = {
      textContainer: lessonPanel.dynamicTextContainer,
      refText: lessonPanel.dialogue_text,
      dynamicDialogueText,
      generatedDialogueTexts: [],
      generatedUnderlines: [],
      generatedPlaceholderIcons: [],
    };

    // Do it for mobile too.
    lessonPanel.dialogue_text_m.setVisible(false);
    const dynamicDialogueText_m = lessonPanel.scene.add.dynamicBitmapText(
      lessonPanel.dialogue_text_m.x,
      lessonPanel.dialogue_text_m.y,
      lessonPanel.dialogue_text_m.font,
      lessonPanel.dialogue_text_m.text,
      lessonPanel.dialogue_text_m.fontSize,
    );
    dynamicDialogueText_m.tintTopLeft = 5387071;
    dynamicDialogueText_m.tintTopRight = 5387071;
    dynamicDialogueText_m.tintBottomLeft = 5387071;
    dynamicDialogueText_m.tintBottomRight = 5387071;
    dynamicDialogueText_m.text =
      'Welcome spellcaster to my realm! Today we are going to be learning about phonemes.';
    dynamicDialogueText_m.fontSize = -20;
    dynamicDialogueText_m.maxWidth = 450;
    lessonPanel.mainContentContainer.add(dynamicDialogueText_m);
    this.dynamicDialogueTextWrapper_m = {
      textContainer: lessonPanel.dynamicTextContainer_m,
      refText: lessonPanel.dialogue_text_m,
      dynamicDialogueText: dynamicDialogueText_m,
      generatedDialogueTexts: [],
      generatedUnderlines: [],
      generatedPlaceholderIcons: [],
    };

    // Option buttons config
    this.getResponsiveElements('dialogueOptionButton0').forEach(
      (dialogueOptionButton: DialogueOptionButton) => {
        dialogueOptionButton.controller.setSubmissionCallback(() => {
          this.runDialogue(
            this.convertScriptItemsToSlides(getLessonPanelSlides()),
          );
        });
      },
    );

    this.getResponsiveElements('dialogueOptionButton1').forEach(
      (dialogueOptionButton: DialogueOptionButton) => {
        dialogueOptionButton.controller.setSubmissionCallback(() => {
          lessonPanel.setVisible(false);
          EventBus.emit(GAME_EVENTS.PLAYER_REQUESTED_OPEN_DAILIES_PANEL);
        });
      },
    );

    this.getResponsiveElements('exitButton').forEach(
      (exitButton: HUDButton) => {
        exitButton.setSubmissionCallback(() => {
          this.close();
        });
      },
    );

    this.getResponsiveElements('exitButton_1').forEach(
      (exitButton: HUDButton) => {
        exitButton.setSubmissionCallback(() => {
          this.close();
        });
      },
    );

    this.getResponsiveElements('replayButton').forEach(
      (replayButton: HUDButton) => {
        replayButton.setSubmissionCallback(() => {
          if (mentorDialogue.isPlaying) return;
          this.replayCurrentDialogueSlide();
        });
      },
    );

    this.getResponsiveElements('speaker_name').forEach(
      (speakerName: Phaser.GameObjects.BitmapText) => {
        speakerName.setText(
          MentorNames[getBiomeFromStudentLevel(getStudent().level)],
        );
      },
    );

    this.initializeResponsiveness();
  }

  // Desktop dynamic texts.
  dynamicDialogueTextWrapper: DynamicTextWrapper;

  // Mobile dynamic texts.
  dynamicDialogueTextWrapper_m: DynamicTextWrapper;

  onDialogueComplete?: () => void;
  currentDialogue?: Phaser.Sound.HTML5AudioSound;
  private dialogueController: AbortController = new AbortController();
  cardGrid?: GridLayoutGroup;
  cardGrid_m?: GridLayoutGroup;
  dialogueInstances: DialogueInstance[] = [];

  getResponsiveElements(key: keyof LessonPanel) {
    return ResponsivenessManager.GetAllResponsiveComponentsFromContainer(
      this.lessonPanel,
      key,
    );
  }

  preloadAssetsForDialogueSlide(dialogueData: DialogueSlide[]) {
    const { lessonPanel } = this;
    const pattern =
      /(<.*?>)|(\/.*?\/)|(\[.*?\])|(\*\s*.*?\s*\*\s*\(\s*.*?\s*\))|(&.*?&\(.*?\))/g;

    dialogueData.forEach((data) => {
      let slideChunks = data.script.split(pattern);
      slideChunks = slideChunks.filter((a) => {
        return a !== undefined;
      });
      slideChunks.forEach((slideChunk) => {
        if (isImageLink(slideChunk)) {
          // Preload the image.
          const path = `${definitionImagesPath}`;
          let imageKey = `${sanitizeLessonMarkup(slideChunk)}`;

          lessonPanel.scene.load.image(imageKey, `${path}${imageKey}.png`);
        }
      });
    });
    lessonPanel.scene.load.start();
  }

  runDialogue(dialogueData: DialogueSlide[]) {
    const { lessonPanel } = this;
    lessonPanel.setVisible(true);

    PhaserAudioManager.setMusicVolume(
      PhaserAudioManager.DEFAULT_MUSIC_VOLUME / 3,
    );

    this.createNewDialogueInstance(dialogueData);
    this.showNextLine();
    lessonPanel.setDepth(9999);

    this.getResponsiveElements('mainContentContainer').forEach(
      (mainContentContainer: Phaser.GameObjects.Container) => {
        mainContentContainer.setVisible(true);
      },
    );

    this.getResponsiveElements('dialogueOptionsContainer').forEach(
      (dialogueOptionsContainer: Phaser.GameObjects.Container) => {
        dialogueOptionsContainer.setVisible(false);
      },
    );

    this.preloadAssetsForDialogueSlide(dialogueData);
  }

  close() {
    if (this.currentDialogue) {
      this.currentDialogue.stop();
    }
    this.abortLessonAudio();
    // Clear dynamic graphics
    this.clearDynamicGraphics();
    // Clear visual aides
    this.cardGrid?.clear();
    this.dialogueInstances = [];
    this.lessonPanel.setVisible(false);
    EventBus.emit(GAME_EVENTS.PLAYER_ENDED_LEVEL_LESSON);
  }

  createNewDialogueInstance(slideData: DialogueSlide[]) {
    this.dialogueInstances.push(new DialogueInstance(slideData));
  }

  showNextLine() {
    if (!this.dialogueInstances || this.dialogueInstances.length === 0) return;
    //this.visualAideIcon.setVisible(false);
    if (this.currentDialogue) {
      this.currentDialogue.stop();
    }
    // Increment the slide, and don't continue if we went past the end.
    if (!this.incrementSlide()) return;

    // Get the currently active script item.
    const currentDialogueInstance = this.getActiveDialogueInstance();
    const slide = currentDialogueInstance.currentSlide();
    const scriptItem = slide.script;

    // Reset visual aides.
    this.cardGrid?.clear();
    this.cardGrid_m?.clear();

    // Play the audio for the slide.
    const slideSplit = this.playAudioForSlide(slide, scriptItem);

    this.generateDynamicText(slideSplit!);
    this.lessonPanel.setVisible(true);
  }

  playAudioForSlide(slide: DialogueSlide, scriptItem: string) {
    const { lessonPanel } = this;
    this.getResponsiveElements('replayButton').forEach(
      (replayButton: HUDButton) => {
        replayButton.setInteractionEnabled(false);
      },
    );

    let audioKeys: { key: string; url: string }[] = [];
    const currentDialogueInstance = this.getActiveDialogueInstance();
    const prevSlide =
      currentDialogueInstance.slides[currentDialogueInstance.index - 1];
    const pattern =
      /(<.*?>)|(\/.*?\/)|(\[.*?\])|(\*\s*.*?\s*\*\s*\(\s*.*?\s*\))|(&.*?&\(.*?\))/g;

    // Reset guardian chunk index if talking about a new subject.
    if (
      currentDialogueInstance.currentSlide().graphemeData?.id !==
      prevSlide?.graphemeData?.id
    ) {
      currentDialogueInstance.guardianChunkIndex = 0;
    }

    let slideSplit = scriptItem.split(pattern);
    slideSplit = slideSplit.filter((a) => {
      return a !== undefined;
    });
    for (let split in slideSplit) {
      let content = slideSplit[split];
      if (
        !content ||
        (!containsAlphabeticalContent(content) && !isPhoneme(content))
      )
        continue;

      let audioKey = '';

      if (isEmphasizedLetter(content)) {
        // We are dealing with a letter.
        const path = '/game_assets/audio/alphabet_audio/';
        const letter: string = content.replace(/<(.*?)>/, '$1');
        audioKey = `${path}${letter}`;
      } else if (isPhoneme(content)) {
        // We are dealing with a phoneme.
        const path = '/game_assets/audio/phoneme_audio/';
        let phoneme = content.replace(/\/(.*?)\//, '$1');
        const g = getScopeAndSequenceGraphemeFromPronunciation(phoneme);
        phoneme = g?.pronunciation || '';
        if (phoneme === '') continue;
        audioKey = `${path}${phoneme}`;
        if (g) {
          content = g.spelling;
        }
      } else if (isMorpheme(content)) {
        // We are dealing with a morpheme.
        const path = '/game_assets/audio/morpheme_audio/';
        let morpheme = content.replace(/\[(.*?)\]/, '$1');
        //content = morpheme;
        morpheme = morpheme.replace('-', '');
        audioKey = `${path}${morpheme}`;
      } else if (isImageLink(content)) {
        // We are dealing with an image link.
        const path = '/game_assets/audio/words_audio/';
        let imageKey = sanitizeLessonMarkup(content);
        audioKey = `${path}${imageKey}`;
      } else {
        const path = '/game_assets/audio/mentors/lessons/';
        const mentor: string =
          MentorPrefixes[getBiomeFromStudentLevel(getStudent().level)];
        let letter: string = slide.graphemeData?.id || '';
        letter = sanitizeFilename(letter); // Make sure morphemes are in an appropriate format.

        audioKey = `${path}${mentor}-${letter}-${currentDialogueInstance.guardianChunkIndex}`;
        currentDialogueInstance.guardianChunkIndex++;
      }
      if (audioKey) {
        audioKeys.push({ key: content, url: audioKey });
      }
    }

    if (this.dialogueController) {
      this.dialogueController.abort();
    }
    this.dialogueController = new AbortController();
    playSoundsInSequence(
      audioKeys,
      mentorDialogue,
      this.dialogueController.signal,
      (chunk) => {
        this.handleDialogueChunkStarted(chunk);
      },
      () => {
        this.getActiveDialogueInstance().currentSlide().chunkIndex = -1;
        this.getResponsiveElements('replayButton').forEach(
          (replayButton: HUDButton) => {
            replayButton.setInteractionEnabled(true);
          },
        );
      },
    );
    return slideSplit;
  }

  overrideMentor(newMentor: string) {
    this.getResponsiveElements('mentorUI').forEach((mentorUI: MentorUI) => {
      mentorUI.setTexture(newMentor);
    });
  }

  handleUserRequestedNextLine() {
    if (!this.dialogueInstances) return;
    this.showNextLine();
  }

  incrementSlide() {
    if (!this.dialogueInstances) return;
    const activeDialogue = this.getActiveDialogueInstance();
    activeDialogue.goToNextSlide();
    if (activeDialogue.hasEnded) {
      this.dialogueInstances = this.dialogueInstances.filter(
        (i) => i !== activeDialogue,
      );
    }

    // Check ending.
    if (this.dialogueInstances.length === 0) {
      // All dialogue instances have ended. Quit out.
      this.onDialogueComplete?.();
      this.lessonPanel.setVisible(false);
      EventBus.emit(GAME_EVENTS.PLAYER_ENDED_LEVEL_LESSON);

      this.abortLessonAudio();

      // Reset visual aides.
      this.cardGrid?.clear();
      return false;
    }
    return true;
  }

  replayCurrentDialogueSlide() {
    const currentDialogueInstance = this.getActiveDialogueInstance();
    const slide = currentDialogueInstance.currentSlide();
    const scriptItem = slide.script;
    this.getActiveDialogueInstance().currentSlide().chunkIndex = -1;
    currentDialogueInstance.guardianChunkIndex =
      currentDialogueInstance.savedGuardianChunkIndex;
    this.playAudioForSlide(slide, scriptItem);
  }

  handleDialogueChunkStarted(chunk: { key: string; url: string }) {
    const { lessonPanel } = this;
    let word = chunk.key;
    let visualAideCard;

    this.getActiveDialogueInstance().currentSlide().chunkIndex++;

    if (isMorpheme(word)) {
      // Create a visual aide for the morpheme.
      // Get the raw morpheme text.
      let start = word.indexOf('[') + 1;
      let end = word.indexOf(']');
      let morpheme = word.substring(start, end);
      visualAideCard = new VisualAideCard(lessonPanel.scene, 0, 0);
      visualAideCard.setText(morpheme);
    } else if (isPhoneme(word) || chunk.url.includes('phoneme_audio')) {
      const currentChunkIndex =
        this.getActiveDialogueInstance().currentSlide().chunkIndex;

      [
        this.dynamicDialogueTextWrapper.generatedPlaceholderIcons,
        this.dynamicDialogueTextWrapper_m.generatedPlaceholderIcons,
      ].forEach((generatedIcons: Phaser.GameObjects.Image[]) => {
        if (generatedIcons[currentChunkIndex]) {
          const icon = generatedIcons[currentChunkIndex];
          DoReinforcementFeedback(lessonPanel.scene, icon);
        }
      });

      // for now: don't show visual aide cards for phonemes.
      // let start = word.indexOf('/') + 1;
      // let end = word.indexOf('/', start);
      // let phoneme = word.substring(start, end);
      // visualAideCard = new VisualAideCard(this.scene, 0, 0);
      // visualAideCard.setText(phoneme);
    } else if (isEmphasizedLetter(word)) {
      let start = word.indexOf('<') + 1;
      let end = word.indexOf('>');
      let letter = word.substring(start, end);

      const existingCards = this.getExistingVisualAideCards(letter);
      if (existingCards.length === 0) {
        [this.cardGrid, this.cardGrid_m].forEach(
          (grid: GridLayoutGroup | undefined) => {
            visualAideCard = new VisualAideCard(lessonPanel.scene, 0, 0);
            visualAideCard.setText(letter);
            grid?.addItem(visualAideCard);
          },
        );
      } else {
        existingCards.forEach((card: VisualAideCard) =>
          card.doReinforcementFeedback(),
        );
      }
    } else if (isImageLink(word)) {
      let start = word.indexOf('(') + 1;
      let end = word.indexOf(')', start);
      let imageKey = word.substring(start, end);
      const existingIcon = this.getExistingVisualAideIcon(imageKey);
      if (!existingIcon) {
        [this.cardGrid, this.cardGrid_m].forEach(
          (grid: GridLayoutGroup | undefined) => {
            visualAideCard = new VisualAideIcon(lessonPanel.scene, 0, 0);
            visualAideCard.setIcon(imageKey);
            grid?.addItem(visualAideCard);
          },
        );
      } else {
        existingIcon.doReinforcementFeedback();
      }
    }
  }

  abortLessonAudio() {
    // Stop current audio and abort any audio that was still loading.
    mentorDialogue.stopAudio();
    this.dialogueController.abort();
    PhaserAudioManager.setMusicVolume(PhaserAudioManager.DEFAULT_MUSIC_VOLUME);
  }

  getExistingVisualAideCards(text: string) {
    const desktopCards = this.cardGrid?.elements
      .filter((e: any) => e instanceof VisualAideCard)
      .filter((card: VisualAideCard) => card.cardTextValue === text);
    const mobileCards = this.cardGrid_m?.elements
      .filter((e: any) => e instanceof VisualAideCard)
      .filter((card: VisualAideCard) => card.cardTextValue === text);
    return [...(desktopCards ?? []), ...(mobileCards ?? [])];
  }

  getExistingVisualAideIcon(icon: string) {
    return this.cardGrid?.elements.find(
      (e) => (e as VisualAideIcon)?.iconTexture === icon,
    ) as VisualAideIcon;
  }

  startNewLesson() {
    if (!GameInstance.instance.getShouldPlayCustomLevel()) {
      EventBus.emit(GAME_EVENTS.PLAYER_VIEWED_LEVEL_LESSON, getStudent().level);
    }
    this.handleResize();
    this.showOptions(); // Showing dialogue options is the first thing we do for a new lesson.
  }

  showOptions(canClose: boolean = true) {
    const { lessonPanel } = this;
    if (!lessonPanel.visible) {
      PhaserAudioManager.playSFX(lessonPanel.scene, 'npc_dialogue_open');
    }
    lessonPanel.setVisible(true);
    this.getResponsiveElements('exitButton').forEach(
      (exitButton: HUDButton) => {
        exitButton.setVisible(canClose);
      },
    );

    this.getResponsiveElements('dialogueOptionsContainer').forEach(
      (dialogueOptionsContainer: Phaser.GameObjects.Container) => {
        dialogueOptionsContainer.setVisible(true);
      },
    );

    const isCustomLevel = GameInstance.instance.getShouldPlayCustomLevel();
    this.getResponsiveElements('dialogueOptionButton0').forEach(
      (dialogueOptionButton: DialogueOptionButton) => {
        dialogueOptionButton.setVisible(!isCustomLevel);
        if (!isCustomLevel) {
          dialogueOptionButton.controller.setText(
            `Level ${getStudent().level} Lesson`,
          );
        }
      },
    );

    this.getResponsiveElements('mainContentContainer').forEach(
      (mainContentContainer: Phaser.GameObjects.Container) => {
        mainContentContainer.setVisible(false);
      },
    );

    this.getResponsiveElements('dialogueOptionButton1').forEach(
      (dialogueOptionButton: DialogueOptionButton) => {
        dialogueOptionButton.setAttentionIconVisible(
          !getDailies().hasViewedDailyActivities,
        );
      },
    );

    this.getResponsiveElements('dialogueOptionButton0').forEach(
      (dialogueOptionButton: DialogueOptionButton) => {
        dialogueOptionButton.setAttentionIconVisible(false);
      },
    );
  }

  getActiveDialogueInstance() {
    return this.dialogueInstances[this.dialogueInstances.length - 1];
  }

  convertScriptItemsToSlides(scriptItems: ScriptItem[]) {
    const result: DialogueSlide[] = [];
    scriptItems.forEach((g) => {
      const graphemeData = g as ScriptGrapheme;
      const morphemeData = g as ScriptMorpheme;

      g.script.forEach((s) => {
        result.push({
          script: s,
          graphemeData,
          morphemeData,
          chunkIndex: -1,
        });
      });
    });
    return result;
  }

  convertMorphemeScriptsToSlides(morphemes: ScriptMorpheme[]) {
    return morphemes.map((g) => {
      const slides = g.script;
      return slides.map((s) => {
        return { script: s } as DialogueSlide;
      });
    });
  }

  clearDynamicGraphics() {
    [
      this.dynamicDialogueTextWrapper,
      this.dynamicDialogueTextWrapper_m,
    ].forEach((dynamicWrapper: DynamicTextWrapper) => {
      for (const text in dynamicWrapper.generatedDialogueTexts) {
        dynamicWrapper.generatedDialogueTexts[text].destroy();
      }
      dynamicWrapper.generatedDialogueTexts = [];

      for (const underline in dynamicWrapper.generatedUnderlines) {
        dynamicWrapper.generatedUnderlines[underline].destroy();
      }
      dynamicWrapper.generatedUnderlines = [];

      for (const placeholder in dynamicWrapper.generatedPlaceholderIcons) {
        dynamicWrapper.generatedPlaceholderIcons[placeholder].destroy();
      }
      dynamicWrapper.generatedPlaceholderIcons = [];
    });
  }

  /*
   * Creates a series of dynamic text objects to make up the dialogue slide. Different text objects are used for different
   * types of content e.g. a clickable related concept.
   */
  generateDynamicText(content: string[]) {
    const { lessonPanel } = this;

    // Hide options.
    this.getResponsiveElements('dialogueOptionsContainer').forEach(
      (dialogueOptionsContainer: Phaser.GameObjects.Container) => {
        dialogueOptionsContainer.setVisible(false);
      },
    );

    // Show main content.
    this.getResponsiveElements('mainContentContainer').forEach(
      (mainContentContainer: Phaser.GameObjects.Container) => {
        mainContentContainer.setVisible(true);
      },
    );
    this.clearDynamicGraphics();

    [
      this.dynamicDialogueTextWrapper,
      this.dynamicDialogueTextWrapper_m,
    ].forEach((dynamicTextWrapper: DynamicTextWrapper) => {
      // Destroy all the previously generated text.
      for (let element in dynamicTextWrapper.generatedDialogueTexts) {
        dynamicTextWrapper.generatedDialogueTexts[element].destroy();
      }
      dynamicTextWrapper.textContainer.list.forEach((child) => {
        dynamicTextWrapper.textContainer.remove(child);
        child.destroy();
      });

      dynamicTextWrapper.dynamicDialogueText.setVisible(false);

      const newDynamicText = createDynamicTextLayout(
        content,
        lessonPanel.scene,
        dynamicTextWrapper.textContainer,
        {
          maxWidth: dynamicTextWrapper.dynamicDialogueText.maxWidth,
          fontSize: dynamicTextWrapper.dynamicDialogueText.fontSize,
          textColor: dynamicTextWrapper.dynamicDialogueText.tint,
        },
      );

      newDynamicText.textArray.forEach((arr) => {
        arr.forEach((t) => {
          this.initializeDynamicText(
            t.textObj,
            t.rawText,
            dynamicTextWrapper.generatedDialogueTexts,
          );
        });
      });

      dynamicTextWrapper.generatedPlaceholderIcons =
        newDynamicText.placeholderIcons;
    });
  }

  createDynamicText(position: Phaser.Math.Vector2, content: string) {
    const newDynamicText = this.lessonPanel.scene.add.dynamicBitmapText(
      position.x,
      position.y,
      'Aronui',
      content,
      -24,
    );
    [
      this.dynamicDialogueTextWrapper,
      this.dynamicDialogueTextWrapper_m,
    ].forEach((dynamicTextWrapper: DynamicTextWrapper) => {
      newDynamicText.setTint(dynamicTextWrapper.dynamicDialogueText.tint);
    });

    return newDynamicText;
  }

  initializeResponsiveness() {
    const { lessonPanel } = this;
    ResponsivenessManager.InitializeResponsiveBreakpoints({
      scene: lessonPanel.scene,
      desktopView: lessonPanel.desktopView,
      mobileView: lessonPanel.mobileView,
    });
    // Add resize handler
    ResponsivenessManager.InitializeResizeHandler(
      lessonPanel.scene,
      () => this.handleResize(),
      this,
    );
  }

  initializeDynamicText(
    text: Phaser.GameObjects.DynamicBitmapText,
    rawText: string,
    newTexts: Phaser.GameObjects.DynamicBitmapText[],
  ) {
    const { lessonPanel } = this;
    if (isRelatedConceptLink(rawText)) {
      text.text.trimStart();
      text.text.trimEnd();
      text.setTint(0x693cbd);
      const newUnderline = underlineBitmapText(
        lessonPanel.scene,
        lessonPanel.mainContentContainer,
        text,
        0x693cbd,
      );
      //this.generatedUnderlines.push(newUnderline);
      //lessonPanel.dynamicTextContainer.add(newUnderline);

      if (isRelatedConceptLink(rawText)) {
        // Interactivity
        text.setInteractive({ cursor: `${HOVER_CURSOR_PATH}, auto` });
        text.on('pointerover', () => {
          text.setTint(0xa39fd6);
          newUnderline.fillStyle(0xa39fd6, 1);
        });
        text.on('pointerout', () => {
          text.setTint(0x693cbd);
          newUnderline.fillStyle(0x693cbd, 1);
        });
        text.on('pointerdown', () => {
          // Delve into the related concept.
          const relatedConcept = getRelatedConceptFromMarkup(rawText);
          // Start a new dialogue.
          this.createNewDialogueInstance([
            createDialogueSlideFromConcept(relatedConcept),
          ]);
          this.showNextLine();
        });
      }

      lessonPanel.scene.textures
        .get(text.texture.key)
        .setFilter(Phaser.Textures.LINEAR);
    }

    newTexts.push(text);
  }

  // Bring this back again if we want to let the user go back in the lesson.
  // handleBackButtonPressed() {
  //   if (!this.dialogueInstances) return;
  //   if (this.dialogueInstances.length > 1) {
  //     this.dialogueInstances.pop();
  //     this.showNextLine();
  //   }
  //   if (this.dialogueInstances.length <= 1) {
  //     this.getResponsiveElements('backButtonContainer').forEach(
  //       (backButtonContainer: Phaser.GameObjects.Container) => {
  //         backButtonContainer.setVisible(false);
  //       },
  //     );
  //   }
  // }

  private async handleResize() {
    const { lessonPanel } = this;
    if (!lessonPanel.scene || !lessonPanel.scene.scene.isActive()) return;
    const anchor = getAnchorPlugin(lessonPanel.scene);
    if (!anchor) return;

    const breakpoint = ResponsivenessManager.GetCurrentGlobalViewForGame(
      lessonPanel.scene,
    );

    // Example: Adjust positioning for smaller screens
    if (breakpoint === 'mobile') {
      anchor.add(lessonPanel.content, {
        centerX: '50%',
        bottom: '100%-100', // Move up a bit on smaller screens
        width: '75%',
      });
      anchor.add(lessonPanel.lessonIconContainer, {
        bottom: '100%-500', // Adjust visual aid position
        x: '50%-256',
      });
    } else {
      // Default positioning for larger screens
      anchor.add(lessonPanel.content, {
        centerX: '50%',
        bottom: '100%-164',
      });
      anchor.add(lessonPanel.lessonIconContainer, {
        bottom: '100%-640',
        x: '50%-256',
      });
    }

    anchor.add(lessonPanel.content_m, {
      bottom: '100%',
      centerX: '50%',
    });
  }

  destroy() {
    const { lessonPanel } = this;
    lessonPanel.destroy();
  }
}
